import { useCallback, useMemo } from 'react';
import { useSelector } from 'react-redux';
import intl from 'react-intl-universal';
import capitalize from 'lodash/capitalize';
import { Option } from 'utils/customTypes';
import {
  REQUEST_LD_PRIORITY,
  REQUEST_LINKED_PROJECTS_OPTIONS,
} from 'utils/constants';
import { ACTIVE_REQUEST_STATUSES_MAPPING } from 'utils/constants/request';
import { RangeFilter, RequestFiltersKey } from 'utils/types/filters';
import { userSummarySelectors } from 'state/normalized/userSummaries/userSummariesSlice';
import { projectSummarySelectors } from 'state/normalized/projectSummaries/projectSummariesSlice';
import { formSummarySelectors } from 'state/normalized/formSummaries/formSummariesSlice';
import moment from 'moment';
import { BusinessUnitSummary, UserSummary } from 'types/store/normalized';
import { isUserStatusDisabled } from 'utils/functions';
import { naturalSort } from 'utils/naturalSorting';
import { ldUserSummarySelectors } from 'state/normalized/userSummaries/LDUserSummariesSlice';
import { businessUnitSummarySelectors } from 'state/normalized/businessUnitSummaries/businessUnitSummariesSlice';

const useActiveRequestFilters = () => {
  const usersList = useSelector(userSummarySelectors.selectAll);
  const ownersList = useSelector(ldUserSummarySelectors.selectAll);
  const businessTeams = useSelector(businessUnitSummarySelectors.selectAll);
  const projects = useSelector(projectSummarySelectors.selectAll);
  const forms = useSelector(formSummarySelectors.selectAll);

  const businessUnitList = useMemo<Option[]>(() => {
    return naturalSort(
      businessTeams?.map((businessUnit: BusinessUnitSummary) => ({
        label: businessUnit.name,
        value: businessUnit.id,
      })),
      'label',
      'asc'
    );
  }, [businessTeams]);

  const activeRequestsUsersList = useMemo<Option[]>(() => {
    return naturalSort(
      usersList?.map((user: UserSummary) => ({
        label: user.name,
        value: user.id,
        disabled: isUserStatusDisabled(user.status),
        avatar: {
          initial: user.avatar?.initial,
          name: user.name,
          imageSrc: user.avatarUrl,
        },
      })),
      'label',
      'asc'
    );
  }, [usersList]);

  // for us to be able to do this we need to have userType in userSummary returned by the API
  const activeRequestsOwnersList = useMemo<Option[]>(() => {
    return naturalSort(
      ownersList?.map((user: UserSummary) => ({
        label: user.name,
        value: user.id,
        disabled: isUserStatusDisabled(user.status),
        avatar: {
          initial: user.avatar?.initial,
          name: user.name,
          imageSrc: user.avatarUrl,
        },
      })),
      'label',
      'asc'
    );
  }, [ownersList]);

  const statusOptions = useMemo<Option[]>(() => {
    const options = Object.keys(ACTIVE_REQUEST_STATUSES_MAPPING).map((key) => ({
      value:
        ACTIVE_REQUEST_STATUSES_MAPPING[
          key as keyof typeof ACTIVE_REQUEST_STATUSES_MAPPING
        ],
      label: intl.get(`REQUESTS_LIST_PAGE.TABLE.STATUS_LABEL.${key}`),
    }));
    return naturalSort(options, 'label', 'asc');
  }, []);

  const priorityOptions = useMemo<Option[]>(() => {
    const options = Object.keys(REQUEST_LD_PRIORITY).map((key) => ({
      value: REQUEST_LD_PRIORITY[key as keyof typeof REQUEST_LD_PRIORITY],
      label: capitalize(key),
    }));
    return naturalSort(options, 'label', 'asc');
  }, []);

  const projectsOptions = useMemo<Option[]>(
    () =>
      projects?.map((project) => ({
        label: project.title,
        value: project.id,
      })),
    [projects]
  );

  const linkedProjectsOptions = useMemo<Option[]>(() => {
    type keys = keyof typeof REQUEST_LINKED_PROJECTS_OPTIONS;
    return Object.keys(REQUEST_LINKED_PROJECTS_OPTIONS).map((key) => ({
      label: intl.get(
        `REQUESTS_LIST_PAGE.TABLE.LINKED_PROJECTS.PICKER_OPTIONS.${key}`
      ),
      value: REQUEST_LINKED_PROJECTS_OPTIONS[key as keys],
    }));
  }, []);

  const formsOptions = useMemo<Option[]>(() => {
    return naturalSort(
      forms?.map((form) => ({
        label: form.title,
        value: form.id,
      })),
      'label',
      'asc'
    );
  }, [forms]);

  const requestFiltersOptions: Record<
    RequestFiltersKey,
    Option[] | RangeFilter | UserSummary[] | BusinessUnitSummary[]
  > = useMemo(
    () => ({
      status: statusOptions,
      requester_id: activeRequestsUsersList,
      requestProjects: projectsOptions,
      owners: activeRequestsUsersList,
      ownersFullList: activeRequestsOwnersList,
      businessTeams: businessUnitList,
      ldPriority: priorityOptions,
      form_id: formsOptions,
      linkedProjects: linkedProjectsOptions,
      createdAt: { from: '', to: '' },
      submittedAt: { from: '', to: '' },
      decision_date: { from: '', to: '' },
      updatedAt: { from: '', to: '' },
    }),
    [
      statusOptions,
      priorityOptions,
      projectsOptions,
      activeRequestsUsersList,
      activeRequestsOwnersList,
      linkedProjectsOptions,
      businessUnitList,
      formsOptions,
    ]
  );

  const getFilterOptionsByKey = useCallback(
    (key: RequestFiltersKey) => requestFiltersOptions[key] as Option[],
    [requestFiltersOptions]
  );

  const getDateFilterLabel = useCallback(
    (value: RangeFilter, dateFormat: string) => {
      let label = '';
      if (value.from) {
        label = `${moment(new Date(value.from)).format(dateFormat)}`;
      }
      if (value.from && value.to) {
        label = `${label} - ${moment(new Date(value.to)).format(dateFormat)}`;
      } else if (!value.from && value.to) {
        label = `${moment(new Date(value.to)).format(dateFormat)}`;
      }
      return label;
    },
    []
  );

  const getFilterOptionLabelByKeyAndValue = useCallback(
    (key: RequestFiltersKey, value: string) => {
      const options = requestFiltersOptions[key] as Option[];
      const option = options.find((option: Option) => option.value === value);
      return option ? option.label : '';
    },
    [requestFiltersOptions]
  );

  return {
    getFilterOptionsByKey,
    getFilterOptionLabelByKeyAndValue,
    getDateFilterLabel,
  };
};

export default useActiveRequestFilters;
